<template>
  <div v-if="loading" class="mt-3">
    <PageLoader
      :title="t('WAIT_A_MOMENT')"
      :subTitle="t('WAIT_VALIDATION_TEXT')"
    />
  </div>
  <div v-else>
    <TermsAndPrivacyKycReuse v-if="showKycReuseFlow" />

    <TermsAndPrivacyEcommerce v-else-if="showEcommerceFlow" />

    <div v-else>
      <Alert
        v-if="showAlert"
        :message="alertMessage"
        :alertType="alertType"
        :alertIcon="alertIcon"
        :closable="alertClosable"
        @onClose="dismissAlert"
        :isSelfService="isSelfService"
      />
      <br v-if="showAlert" />

      <Dropdown
        v-if="showLanguageDropdown"
        :class="{ 'fs-3': isSelfService }"
        :dropdown-Label="t('SELECT_CUSTOMER_LANGUAGE')"
        :options="customerLanguageList"
        :placeholder="t('SELECT_CUSTOMER_LANGUAGE')"
        :defaultValue="customerDefaultLanguage"
        :isSelfService="isSelfService"
        @update:modelValue="handleLanguageChange"
      />

      <div class="title" :class="{ 'fs-1': isSelfService }">
        {{ t("PAGE_TITLE_PRIVACY_AND_TERMS") }}
      </div>
      <div class="privacy-terms-text" :class="{ 'fs-3': isSelfService }">
        {{ t("ACCEPT_PRIVACY_TERMS_TEXT") }}
      </div>

      <TermsAndPrivacyContentInline
        :styleForSelfService="isSelfService"
        :cartTag="cartTag"
        :locale="customerLocaleCode"
        :requiresManualAcknowledgement="requiresManualAcknowledgementFromConfig"
        @privacy-acceptance-changed="onPrivacyPolicyAcceptanceChange"
        @terms-acceptance-changed="onTermsAndConditionsAcceptanceChange"
      />

      <div class="py-2">
        <ButtonContinue
          class="btn-next"
          :class="{ 'fs-3 pj-ss-btn': isSelfService }"
          :isEnabled="allowContinue"
          @callback="handleAcceptPrivacyAndTerms"
        >
          <i v-if="acceptingPrivacyAndTerms" class="fa fa-spin fa-spinner"></i>
          <span v-else>{{ t("CONTINUE") }}</span>
        </ButtonContinue>
      </div>

      <PreviousStepButton @route-changed="handleNavigateToPreviousStep" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { AxiosError, AxiosResponse } from "axios";
import { datadogRum } from "@datadog/browser-rum";
import { IOptionDropdown } from "@/types/dropdown";
import { ILanguage } from "@/apps/merchant3/models/ILanguages";
import { getLanguages } from "@/apps/merchant3/services/languages.service";
import { getAPIErrorMessage } from "@/apps/merchant3/shared-helpers/APIErrorMessages";
import { clearStoreAndNavigateHome } from "@/apps/merchant3/helpers/redirect.helper";
import { useAlert } from "@/apps/merchant3/hooks/useAlert";
import { useKycReuse } from "@/apps/merchant3/hooks/useKycReuse";
import { useCustomerLocale } from "@/apps/merchant3/hooks/useCustomerLocale";
import { useTermsAndPrivacyAcceptance } from "@/apps/merchant3/hooks/useTermsAndPrivacyAcceptance";
import useConfigurations from "@/apps/merchant3/hooks/useConfigurations";
import { useConfigurableNavigation } from "@/apps/merchant3/hooks/useConfigurableNavigation";
import useMerchantPersistState from "@/apps/merchant3/hooks/useMerchantPersistState";
import useUpdatePageState from "@/apps/merchant3/hooks/useUpdateCurrentPageState";
import Alert from "@/apps/merchant3/shared-components/common/Alert.vue";
import Dropdown from "@/apps/merchant3/shared-components/ui/Dropdown.vue";
import ButtonContinue from "@/apps/merchant3/shared-components/ui/ButtonContinue.vue";
import PreviousStepButton from "@/apps/merchant3/shared-components/ui/PreviousStepButton.vue";
import PageLoader from "@/apps/merchant3/components/ui/loaders/PageLoader.vue";
import TermsAndPrivacyKycReuse from "@/apps/merchant3/components/customer-registration/terms-and-privacy/TermsAndPrivacyKycReuse.vue";
import TermsAndPrivacyEcommerce from "@/apps/merchant3/components/customer-registration/terms-and-privacy/TermsAndPrivacyEcommerce.vue";
import TermsAndPrivacyContentInline from "@/apps/merchant3/components/customer-registration/terms-and-privacy/TermsAndPrivacyContentInline.vue";

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const { components, flows, isConfigLoaded } = useConfigurations();
const { navigateToNextStep } = useConfigurableNavigation();
const { isKycReuseEnabled } = useKycReuse();
const { customerLocaleCode, customerDefaultLanguage, setCustomerLocale } =
  useCustomerLocale();
const {
  alertClosable,
  alertIcon,
  alertMessage,
  alertType,
  showAlert,
  dismissAlert,
} = useAlert();
const {
  hasAcknowledgedTermsAndConditions,
  hasAcknowledgedPrivacyPolicy,
  hasAcknowledgedPrivacyAndTerms,
  acceptPrivacyAndTerms,
  acceptingPrivacyAndTerms,
} = useTermsAndPrivacyAcceptance();

const cartTag = computed(() => store.state.carts.cartTag);

const isSelfService = computed(() => {
  return flows.value?.isSelfOnboarding ?? false;
});

const isEcommerce = computed(() => {
  return flows.value?.isEcommerce ?? false;
});

const showKycReuseFlow = computed(() => {
  return !cartTag.value || isKycReuseEnabled.value;
});

const showEcommerceFlow = computed(() => {
  return !showKycReuseFlow.value && isEcommerce.value;
});

const showLanguageDropdown = computed(() => {
  return components.value?.documentLanguageDropdown ?? false;
});

const requiresManualAcknowledgementFromConfig = computed(() => {
  return components.value?.manualAcknowledgement ?? false;
});

const allowContinue = computed<boolean>(() => {
  return requiresManualAcknowledgementFromConfig.value
    ? hasAcknowledgedPrivacyAndTerms.value
    : !acceptingPrivacyAndTerms.value;
});

const loading = ref(true);

watch(
  [isConfigLoaded, cartTag],
  ([configLoaded, cart]) => {
    if (!cart && configLoaded) {
      loading.value = false;
      return;
    }
    if (configLoaded && cart) {
      loading.value = false;
    }
  },
  { immediate: true }
);

const customerLanguageList = ref([] as IOptionDropdown[]);

const initLanguages = (): void => {
  if (showLanguageDropdown.value) {
    getLanguages()
      .then((response: AxiosResponse) => {
        response.data.languages.forEach((language: ILanguage) => {
          customerLanguageList.value.push({
            code: language.locale,
            name: language.name,
          });
        });
      })
      .catch((error: AxiosError) => {
        alertType.value = "alert-danger";
        alertMessage.value = getAPIErrorMessage(error.response?.data, t);
        showAlert.value = true;
      });
  }
};

const handleLanguageChange = (value: IOptionDropdown) => {
  setCustomerLocale(value, () => router.go(0));
};

const onPrivacyPolicyAcceptanceChange = (value: boolean) => {
  hasAcknowledgedPrivacyPolicy.value = value;
  store.dispatch("person/setPrivacyPolicyAccepted", value);
};

const onTermsAndConditionsAcceptanceChange = (value: boolean) => {
  hasAcknowledgedTermsAndConditions.value = value;
  store.dispatch("person/setTermsAndConditionsAccepted", value);
};

const handleAcceptPrivacyAndTerms = () => {
  acceptPrivacyAndTerms(
    cartTag.value,
    () => {
      navigateToNextStep();
    },
    (message, _error) => {
      alertType.value = "alert-danger";
      alertMessage.value = message;
      showAlert.value = true;
    }
  );
};

const handleNavigateToPreviousStep = () => {
  datadogRum.addAction("m3-navigated-to-previous-checkout-step-clicked", {
    merchantId: store.state.merchant?.merchant?.merchantId,
    salesClerkId: store.state.clerk?.clerk?.salesClerkId,
    cartTag: store.state.carts?.cartTag,
  });

  clearStoreAndNavigateHome();
};

onMounted(() => {
  useUpdatePageState();
  useMerchantPersistState(() => {
    initLanguages();
    return true;
  });
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
.title {
  color: $pj-color-green;
  font-weight: bold;
  font-size: 1.75rem;
}
.privacy-terms-text {
  font-size: 1rem;
}
</style>
